/**
 * @krisspy-file
 * @type page
 * @name "InventoryList"
 * @title "Inventory List"
 * @description "View and manage inventory items for all products"
 * @routes ["/inventory"]
 * @layout "DashboardLayout"
 * @flowName "inventory-management"
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MapPin,
  Package,
  Download,
  Copy,
  Tag,
  Clock,
  AlertCircle,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { getInventoryItems } from '../services/inventory.service';
import type { InventoryItemWithProduct } from '../services/inventory.service';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useBarcodeScanning, useScanHandler } from '../contexts/ScanContext';
import { initScrollFix, cleanupScrollFix } from '../lib/scrollFix';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useUrlFilters } from '../lib/useUrlFilters';

export default function InventoryList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { enabled: scanningEnabled } = useBarcodeScanning();
  // While the search field has focus, a scan searches for that barcode
  // instead of opening the booking flow.
  const [searchFocused, setSearchFocused] = useState(false);

  // Use URL-based filters for persistence across navigation
  const { getFilter, setFilter } = useUrlFilters({
    defaults: {
      search: '',
      product: 'all',
      status: 'all',
      location: 'all',
      sortBy: 'identifier',
      sortDir: 'asc'
    },
    storageKey: 'inventory-filters'
  });

  // Derive filter values from URL
  const searchQuery = getFilter('search');
  const productFilter = getFilter('product') || 'all';
  const statusFilter = getFilter('status') || 'all';
  const locationFilter = getFilter('location') || 'all';
  const sortBy = (getFilter('sortBy') || 'identifier') as 'identifier' | 'product_name';
  const sortDirection = (getFilter('sortDir') || 'asc') as 'asc' | 'desc';

  // Setter functions that update URL
  const setSearchQuery = (value: string) => setFilter('search', value);
  useScanHandler((code) => setSearchQuery(code), searchFocused);
  const setProductFilter = (value: string) => setFilter('product', value);
  const setStatusFilter = (value: string) => setFilter('status', value);
  const setLocationFilter = (value: string) => setFilter('location', value);
  const setSortBy = (value: 'identifier' | 'product_name') => setFilter('sortBy', value);
  const setSortDirection = (value: 'asc' | 'desc') => setFilter('sortDir', value);

  // State for real data
  const [inventoryItems, setInventoryItems] = useState<InventoryItemWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<string[]>(['all']);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);

  // Load inventory items on mount, when filters change, and when returning from delete
  useEffect(() => {
    loadInventory();
  }, [location.state?.reload, statusFilter, locationFilter]);

  // Initialize scroll fix for tables
  useEffect(() => {
    const timer = setTimeout(() => {
      initScrollFix();
    }, 100);

    return () => {
      clearTimeout(timer);
      cleanupScrollFix();
    };
  }, [inventoryItems]);

  async function loadInventory() {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getInventoryItems(
      { merchantId, locationIds, isPlatformAdmin },
      {
        locationId: locationFilter !== 'all' ? locationFilter : undefined,
        active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        searchQuery: searchQuery || undefined,
        searchBarcode: scanningEnabled,
      }
    );

    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    if (data) {
      setInventoryItems(data);

      // Extract unique product names
      const uniqueProducts = Array.from(
        new Set(data.map((item: any) => item.product_name).filter(Boolean))
      ) as string[];
      setProducts(['all', ...uniqueProducts]);

      // Extract unique locations
      const uniqueLocations = Array.from(
        new Map<string, { id: string; name: string }>(
          data
            .filter((item: any) => item.location_id && item.location_name)
            .map((item: any) => [item.location_id, { id: item.location_id!, name: item.location_name! }])
        ).values()
      );
      // Only update the locations list when not filtering by location,
      // so the dropdown keeps showing all options when one is selected
      if (locationFilter === 'all') {
        setLocations(uniqueLocations);
      }
    }

    setLoading(false);
  }

  // Filter and sort inventory items locally
  const filteredItems = inventoryItems
    .filter((item) => {
      const matchesSearch = !searchQuery ||
        item.item_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.product_name && item.product_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (scanningEnabled && !!item.barcode && item.barcode.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesProduct = productFilter === 'all' || item.product_name === productFilter;

      const matchesLocation = locationFilter === 'all' || item.location_id === locationFilter;

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && item.active) ||
        (statusFilter === 'rented' && item.is_currently_rented) ||
        (statusFilter === 'blocked' && item.is_currently_blocked) ||
        (statusFilter === 'maintenance' && !item.active);

      return matchesSearch && matchesProduct && matchesLocation && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'identifier') {
        comparison = a.item_number.localeCompare(b.item_number);
      } else if (sortBy === 'product_name') {
        const nameA = a.product_name || '';
        const nameB = b.product_name || '';
        comparison = nameA.localeCompare(nameB);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const handleSort = (column: 'identifier' | 'product_name') => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (item: InventoryItemWithProduct) => {
    if (!item.active) return 'badge-danger';
    if (item.is_currently_blocked) return 'badge-warning';
    if (item.is_currently_rented) return 'badge-info';
    return 'badge-success';
  };

  const getStatusText = (item: InventoryItemWithProduct) => {
    if (!item.active) return t('common.inactive');
    if (item.is_currently_blocked) return t('inventory.blocked');
    if (item.is_currently_rented) return t('inventory.rented');
    return t('inventory.available');
  };

  const exportToCSV = async () => {
    if (filteredItems.length === 0) return;

    // Collect all alt/additional product type IDs referenced by filtered items
    const altProductTypeIds = new Set<string>();
    for (const item of filteredItems) {
      if (item.alt_product_type_id) altProductTypeIds.add(item.alt_product_type_id);
      if (item.additional_product_type_ids) {
        for (const id of item.additional_product_type_ids) altProductTypeIds.add(id);
      }
    }

    // Batch-fetch referenced product types and their locations
    const productTypeMap = new Map<string, { product_name: string; location_id: string; available_location_ids: string[] | null }>();
    const locationMap = new Map<string, string>();

    if (altProductTypeIds.size > 0) {
      const { data: productTypes } = await supabase
        .from('product_types')
        .select('id, product_name, location_id, available_location_ids')
        .in('id', Array.from(altProductTypeIds));

      if (productTypes) {
        const allLocationIds = new Set<string>();
        for (const pt of productTypes) {
          productTypeMap.set(pt.id, { product_name: pt.product_name, location_id: pt.location_id, available_location_ids: pt.available_location_ids });
          allLocationIds.add(pt.location_id);
          if (pt.available_location_ids) {
            for (const locId of pt.available_location_ids) allLocationIds.add(locId);
          }
        }

        if (allLocationIds.size > 0) {
          const { data: locs } = await supabase
            .from('locations')
            .select('id, location_name')
            .in('id', Array.from(allLocationIds));
          if (locs) {
            for (const loc of locs) locationMap.set(loc.id, loc.location_name);
          }
        }
      }
    }

    // Format alt product type info for a single product type ID
    const formatProductTypeInfo = (ptId: string): string => {
      const pt = productTypeMap.get(ptId);
      if (!pt) return ptId; // fallback to ID if not found
      const primaryLoc = locationMap.get(pt.location_id) || '';
      const availableLocs = (pt.available_location_ids || [])
        .map(id => locationMap.get(id))
        .filter(Boolean);
      let result = `${pt.product_name} @ ${primaryLoc}`;
      if (availableLocs.length > 0) {
        result += ` (available at: ${availableLocs.join(', ')})`;
      }
      return result;
    };

    // Build the alt product type names string for an item
    const getAltProductTypeNames = (item: InventoryItemWithProduct): string => {
      const parts: string[] = [];
      if (item.alt_product_type_id) {
        parts.push(formatProductTypeInfo(item.alt_product_type_id));
      }
      if (item.additional_product_type_ids) {
        for (const id of item.additional_product_type_ids) {
          parts.push(formatProductTypeInfo(id));
        }
      }
      return parts.join('; ');
    };

    const headers = [
      'Item Number',
      'Product Type ID',
      'Product Name',
      'Size',
      'Location',
      'Status',
      'Combination Code',
      'Backup Code',
      'Allocation Order',
      'Usage (Rentals)',
      'Usage (Hours)',
      'Usage (Revenue)',
      'Last Service',
      'Next Service',
      'Active',
      'Pre-booking Hours',
      'Cutoff Days Prior',
      'Cutoff Time',
      'Alternative Product Type Names and Locations',
      // Appended last and only with scanning on, so the columns of existing
      // exports keep their positions.
      ...(scanningEnabled ? ['Barcode'] : []),
    ];

    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const rows = filteredItems.map((item) => [
      item.item_number || '',
      item.product_type_id || '',
      item.product_name || '',
      item.size || '',
      item.location_name || '',
      getStatusText(item),
      item.combination_code || '',
      item.backup_code || '',
      item.allocation_order?.toString() || '',
      (item.usage_times || 0).toString(),
      (item.usage_hours || 0).toString(),
      (item.usage_revenue || 0).toString(),
      item.last_maintenance_date || '',
      item.next_maintenance_date || '',
      item.active ? 'Yes' : 'No',
      (item.pre_booking_hours ?? '').toString(),
      (item.cutoff_days_prior ?? '').toString(),
      item.cutoff_time || '',
      getAltProductTypeNames(item),
      ...(scanningEnabled ? [item.barcode || ''] : []),
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="page-header" data-testid="inventory-page-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">{t('inventory.title')}</h1>
          <p className="text-secondary">{t('inventory.description')}</p>
        </div>
        <div className="page-header-actions" data-testid="inventory-page-header-actions">
          <button
            onClick={exportToCSV}
            className="btn btn-ghost"
            disabled={loading || filteredItems.length === 0}
          >
            <Download className="w-4 h-4" />
            {t('inventory.exportCsv')}
          </button>
          <button
            onClick={() => navigate('/inventory/create')}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.addInventory')}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="card bg-danger/10 border border-danger/20">
          <div className="flex items-center gap-md">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-danger mb-xs">{t('inventory.errorLoading')}</h4>
              <p className="text-sm text-danger/80">{error}</p>
            </div>
            <button
              onClick={loadInventory}
              className="btn btn-ghost text-danger hover:bg-danger/10"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-lg">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                type="text"
                placeholder={t('inventory.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          {/* Product Filter */}
          <div className="w-full lg:w-48">
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                {products.map((prod) => (
                  <option key={prod} value={prod}>
                    {prod === 'all' ? t('products.allProducts') : prod}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Location Filter */}
          <div className="w-full lg:w-48">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                <option value="all">{t('products.allLocations')}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full lg:w-48">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                <option value="all">{t('inventory.allStatus')}</option>
                <option value="active">{t('inventory.available')}</option>
                <option value="rented">{t('inventory.rented')}</option>
                <option value="blocked">{t('inventory.blocked')}</option>
                <option value="maintenance">{t('common.inactive')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card">
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-md text-secondary">{t('inventory.loadingInventory')}</span>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      {!loading && (
        <div className="card overflow-hidden">
          {filteredItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b border-primary">
                  <tr>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">
                      <button
                        onClick={() => handleSort('identifier')}
                        className="flex items-center gap-xs hover:text-blue transition-colors"
                      >
                        {t('inventory.identifier')}
                        {sortBy === 'identifier' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                        ) : (
                          <ArrowUpDown className="w-4 h-4 opacity-30" />
                        )}
                      </button>
                    </th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">
                      <button
                        onClick={() => handleSort('product_name')}
                        className="flex items-center gap-xs hover:text-blue transition-colors"
                      >
                        {t('inventory.productName')}
                        {sortBy === 'product_name' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                        ) : (
                          <ArrowUpDown className="w-4 h-4 opacity-30" />
                        )}
                      </button>
                    </th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventory.size')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventory.location')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventory.status')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventory.combination')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventory.usage')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventory.lastService')}</th>
                    <th className="py-md px-lg text-sm font-semibold text-primary">
                      <span className="sr-only">{t('inventoryForm.duplicate')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredItems.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-secondary transition-colors cursor-pointer"
                      onClick={() => navigate(`/inventory/${item.id}/edit`)}
                    >
                      <td className="py-md px-lg">
                        <div className="flex items-center gap-xs">
                          <Tag className="w-4 h-4 text-secondary" />
                          <span className="text-sm font-medium text-primary">{item.item_number}</span>
                        </div>
                        {scanningEnabled && item.barcode && (
                          <div className="text-xs text-secondary font-mono mt-xs">{item.barcode}</div>
                        )}
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-sm text-primary">{item.product_name || t('common.na')}</span>
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-sm text-secondary">{item.size || '-'}</span>
                      </td>
                      <td className="py-md px-lg">
                        <div className="flex items-center gap-xs">
                          <MapPin className="w-4 h-4 text-secondary" />
                          <span className="text-sm text-secondary">{item.location_name || t('common.na')}</span>
                        </div>
                      </td>
                      <td className="py-md px-lg">
                        <span className={`badge ${getStatusBadge(item)}`}>
                          {getStatusText(item)}
                        </span>
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-sm font-mono text-secondary">
                          {item.combination_code || t('common.na')}
                        </span>
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-sm text-primary">{item.usage_times || 0} {t('inventory.rentals')}</span>
                      </td>
                      <td className="py-md px-lg">
                        <div className="flex items-center gap-xs">
                          <Clock className="w-4 h-4 text-secondary" />
                          <span className="text-sm text-secondary">
                            {item.last_maintenance_date
                              ? formatDate(item.last_maintenance_date)
                              : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="py-md px-lg text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            // The row itself opens the editor.
                            e.stopPropagation();
                            navigate(`/inventory/create?from=${item.id}`);
                          }}
                          className="btn-icon"
                          title={t('inventoryForm.duplicate')}
                          aria-label={t('inventoryForm.duplicate')}
                          data-testid={`inventory-duplicate-${item.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-2xl">
              <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
                <Package className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-sm">{t('inventory.noItemsFound')}</h3>
              <p className="text-secondary text-sm mb-xl">
                {error ? t('inventory.unableToLoad') : t('inventory.tryAdjustingFilters')}
              </p>
              <button
                onClick={() => navigate('/inventory/create')}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" />
                {t('inventory.addInventory')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results Summary */}
      {!loading && filteredItems.length > 0 && (
        <div className="flex items-center justify-between text-sm text-secondary">
          <p>
            {t('common.showing')} <span className="font-medium text-primary">{filteredItems.length}</span> {t('common.of')}{' '}
            <span className="font-medium text-primary">{inventoryItems.length}</span> {t('common.items')}
          </p>
          <div className="flex items-center gap-lg">
            <p>
              {t('inventory.available')}: <span className="font-medium text-success">
                {filteredItems.filter(i => i.active && !i.is_currently_rented && !i.is_currently_blocked).length}
              </span>
            </p>
            <p>
              {t('inventory.rented')}: <span className="font-medium text-info">
                {filteredItems.filter(i => i.is_currently_rented).length}
              </span>
            </p>
            <p>
              {t('inventory.blocked')}: <span className="font-medium text-warning">
                {filteredItems.filter(i => i.is_currently_blocked).length}
              </span>
            </p>
            <p>
              {t('common.inactive')}: <span className="font-medium text-danger">
                {filteredItems.filter(i => !i.active).length}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
